/**
 * predictive-search-tabs.js
 *
 * Self-contained controller for the BPZ live-suggestions dropdown.
 * Does NOT depend on the theme's global.js custom element registry
 * (which isn't loaded in this theme). Instead, listens directly to
 * any input inside <predictive-search class="predictive-search-bar">,
 * debounces, fetches /search/suggest, and injects the response HTML
 * into the local #predictive-search panel.
 *
 * Also handles:
 *   - Tab switching (Products / Brands / Collections) via delegation
 *   - Outside-click close
 *   - Escape-key close
 */
(function () {
  'use strict';

  var DEBOUNCE_MS = 200;
  var SUGGEST_URL = '/search/suggest';
  var SECTION_ID = 'predictive-search';

  /**
   * Cancellable debounced fetch wrapper, scoped per <predictive-search> root.
   */
  function makeFetcher(root) {
    var timer = null;
    var controller = null;
    var lastQuery = null;

    function clear() {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      if (controller) {
        controller.abort();
        controller = null;
      }
    }

    function run(query) {
      query = (query || '').trim();
      var panel = root.querySelector('#predictive-search');
      if (!panel) return;

      if (query.length === 0) {
        clear();
        panel.innerHTML = '';
        lastQuery = null;
        return;
      }

      if (query === lastQuery) return;

      clear();
      timer = setTimeout(function () {
        controller = new AbortController();
        var url =
          SUGGEST_URL +
          '?q=' +
          encodeURIComponent(query) +
          '&section_id=' +
          SECTION_ID +
          '&resources[type]=product,article,collection,query' +
          '&resources[limit]=10' +
          '&resources[options][unavailable_products]=last';

        fetch(url, { signal: controller.signal })
          .then(function (res) {
            if (!res.ok) throw new Error('Suggest fetch failed: ' + res.status);
            return res.text();
          })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var wrapper = doc.querySelector('#predictive-search-results-wrapper');
            panel.innerHTML = wrapper ? wrapper.outerHTML : '';
            lastQuery = query;
          })
          .catch(function (err) {
            if (err.name === 'AbortError') return;
            // Soft-fail; don't break the form
            console.warn('[predictive-search]', err);
          });
      }, DEBOUNCE_MS);
    }

    return { run: run, clear: clear };
  }

  /**
   * Initialise each <predictive-search> root on the page.
   */
  function initRoot(root) {
    if (root.dataset.psInit === 'true') return;
    root.dataset.psInit = 'true';

    var input = root.querySelector('input[type="search"][name="q"]');
    if (!input) return;

    var fetcher = makeFetcher(root);

    input.addEventListener('input', function () {
      fetcher.run(input.value);
    });

    input.addEventListener('focus', function () {
      // Re-run if there's already content (e.g. results page where input has search.terms)
      if (input.value.trim().length > 0) fetcher.run(input.value);
    });

    // Esc closes the panel
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        var panel = root.querySelector('#predictive-search');
        if (panel) panel.innerHTML = '';
        input.blur();
      }
    });
  }

  function initAll() {
    document.querySelectorAll('predictive-search.predictive-search-bar').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // --- Tab switching via delegation -----------------------------------
  document.addEventListener('click', function (event) {
    var tab = event.target.closest('[data-predictive-tab]');
    if (!tab) return;
    if (tab.disabled || tab.classList.contains('is-disabled')) {
      event.preventDefault();
      return;
    }
    event.preventDefault();

    var wrapper = tab.closest('[data-bpz-predictive-results]');
    if (!wrapper) return;

    var targetTab = tab.getAttribute('data-predictive-tab');

    wrapper.querySelectorAll('[data-predictive-tab]').forEach(function (btn) {
      var isActive = btn.getAttribute('data-predictive-tab') === targetTab;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    wrapper.querySelectorAll('[data-predictive-panel]').forEach(function (panel) {
      var match = panel.getAttribute('data-predictive-panel') === targetTab;
      if (match) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });
  });

  // --- Outside-click close --------------------------------------------
  document.addEventListener('click', function (event) {
    if (event.target.closest('predictive-search')) return;
    document.querySelectorAll('predictive-search.predictive-search-bar').forEach(function (root) {
      var panel = root.querySelector('#predictive-search');
      if (panel) panel.innerHTML = '';
    });
  });
})();
